import { StyleSheet, View } from 'react-native';
import { Typography } from 'ucl';

import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants';

import { ILoyaltyBadgeProgress } from './types';
import { getBadgeProgressPercentage } from './utils';

interface BadgeProgressBarProps {
  progress: ILoyaltyBadgeProgress;
  variant?: 'compact' | 'detail';
}

export const BadgeProgressBar = ({ progress, variant = 'compact' }: BadgeProgressBarProps) => {
  const { currentValue, targetValue } = progress;
  const percentage = getBadgeProgressPercentage({ currentValue, targetValue });

  const variantStyles = variantStylesMap[variant];
  const barHeight = variantConfig[variant].barHeight;
  const trackColor = variantConfig[variant].trackColor;

  const accessibilityLabel = t('badgeProgressLabel', {
    current: currentValue,
    target: targetValue,
    defaultValue: '{current} of {target} completed',
  });

  return (
    <View style={variantStyles.container} accessible accessibilityLabel={accessibilityLabel}>
      <View style={[sharedStyles.track, { height: barHeight, backgroundColor: trackColor }]}>
        <View style={[sharedStyles.fill, { width: `${percentage}%`, height: barHeight }]} />
      </View>

      {variant === 'detail' ? (
        <Typography style={variantStyles.text}>
          <Typography bold>{currentValue}</Typography>
          {` ${t('of', 'of')} `}
          <Typography bold>{targetValue}</Typography>
          {progress.itemLabel ? ` ${progress.itemLabel}` : ''}
          {` ${t('down', 'down')} `}
          {progress.encouragingPhrase ? `\u2014 ${progress.encouragingPhrase}` : ''}
        </Typography>
      ) : (
        <Typography style={variantStyles.text} numberOfLines={1}>
          {progress.itemLabel
            ? t('badgeProgressDownWithLabel', {
                current: currentValue,
                target: targetValue,
                itemLabel: progress.itemLabel,
                defaultValue: '{current} of {target} {itemLabel} down',
              })
            : t('badgeProgressDown', {
                current: currentValue,
                target: targetValue,
                defaultValue: '{current} of {target} down',
              })}
        </Typography>
      )}
    </View>
  );
};

const sharedStyles = StyleSheet.create({
  track: {
    width: '100%',
    borderRadius: 8,
    overflow: 'hidden',
  },
  fill: {
    backgroundColor: primitive.$meltyYellow,
    borderRadius: 999,
  },
});

const variantConfig = {
  compact: { barHeight: 4, trackColor: primitive.$blackOpacity10 },
  detail: { barHeight: 8, trackColor: primitive.$white },
} as const;

const variantStylesMap = {
  compact: StyleSheet.create({
    container: {
      width: '100%',
      alignItems: 'center',
      paddingHorizontal: 3,
    },
    text: {
      fontSize: primitive.fontSizes.xs,
      textAlign: 'center',
      color: primitive.$blackOpacity80,
      fontFamily: primitive.fontFamily.$body,
    },
  }),
  detail: StyleSheet.create({
    container: {
      marginTop: 10,
      width: '100%',
      maxWidth: 500,
      alignSelf: 'center',
      alignItems: 'center',
      gap: 8,
    },
    text: {
      fontSize: primitive.fontSizes.sm,
      textAlign: 'center',
      color: primitive.$bbqBrown,
      lineHeight: 18,
    },
  }),
} as const;
